import {
  CheckOutlined,
  ErrorOutlineOutlined,
  InfoOutlined,
  WarningOutlined,
} from '@material-ui/icons'
import {
  BasicToastrOptions,
  ConfirmToastrOptions,
  toastr,
} from 'react-redux-toastr'
import { action } from 'typesafe-actions'
import { BuiltInThemes, darkBlueTheme, rh24WebAppTheme } from 'utils/appTheme'
import { Issue } from '../../model/IssueContext'
import { AuthService } from '../../services/auth'
import { APICallArgs, ApplicationActionTypes } from './appState'
import i18n from 'i18next'

export function APICall(configuration: APICallArgs) {
  return action(ApplicationActionTypes.API, configuration)
}

export const ToggleNewItemsModal = (open?: boolean) => (dispatch) => {
  dispatch({
    type: ApplicationActionTypes.ToggleNewItemsModal,
    open,
  })
}

export const GetAccessToken = async () => {
  const promise = new Promise<string>(async (resolve) => {
    let retries = 0
    let authToken = undefined
    do {
      authToken = await AuthService.GetCurrent()?.GetAccessToken()
      !authToken && await new Promise((r) => setTimeout(r, 1000))
      retries++
    } while (retries < 3 && !authToken)

    resolve(authToken)
  })

  return promise
}

export const StartNetwork = (payload = 'global') =>
  action(ApplicationActionTypes.StartNetwork, payload)

export const EndNetwork = (payload = 'global') =>
  action(ApplicationActionTypes.EndNetwork, payload)

export const SetSignalRInfo = (payload: {
  [connectionGroup: string]: {
    isConnected: boolean
    isReconnecting: boolean
    errorMessage?: string
  }
}) => action(ApplicationActionTypes.SignalRSubscriptionInfo, payload)

export const ShowUserFeedbackForm = ({
  show,
  issue,
}: {
  show: boolean
  issue: Issue
}) => ({
  type: ApplicationActionTypes.ShowUserFeedbackForm,
  show: show,
  issue,
})

export const ShowNewProjectForm = (show: boolean, isSellerRole: boolean) => ({
  type: ApplicationActionTypes.ShowNewProjectForm,
  show,
  isSellerRole,
})

export const SuccessMessage = (
  title: string,
  message: string,
  options: BasicToastrOptions = {}
) => {
  toastr.success(title, message, {
    className: 'qf-toastr qf-toastr-success',
    icon: <CheckOutlined style={{ width: '32px', height: '32px' }} />,
    transitionIn: 'fadeIn',
    transitionOut: 'fadeOut',
    showCloseButton: false,
    ...options,
  })
}

export const InfoMessage = (
  title: string,
  message: string,
  options: BasicToastrOptions = {}
) => {
  toastr.info(title, message, {
    className: 'qf-toastr qf-toastr-info',
    icon: <InfoOutlined style={{ width: '32px', height: '32px' }} />,
    transitionIn: 'fadeIn',
    transitionOut: 'fadeOut',
    showCloseButton: false,
    ...options,
  })
}

export const ConfirmMessage = (
  message: string,
  options: ConfirmToastrOptions = {}
) => {
  toastr.confirm(message, options)
}

export const WarningMessage = (
  title: string,
  message: string,
  options: BasicToastrOptions = {}
) => {
  toastr.warning(title, message, {
    className: 'qf-toastr qf-toastr-warning',
    icon: <WarningOutlined style={{ width: '32px', height: '32px' }} />,
    transitionIn: 'fadeIn',
    transitionOut: 'fadeOut',
    showCloseButton: false,
    ...options,
  })
}

export const ErrorMessage = (
  title: string,
  message: string | unknown,
  options: BasicToastrOptions = {}
) => {
  console.error(`${title}: ${message}`)

  if (message?.toString().length > 200) {
    message = i18n.t(
      'errors:internal-error-try-later',
      'internal error, please retry in a few moments'
    )
  }

  toastr.error(title, message as string, {
    className: 'qf-toastr qf-toastr-error',
    icon: <ErrorOutlineOutlined style={{ width: '32px', height: '32px' }} />,
    transitionIn: 'fadeIn',
    transitionOut: 'fadeOut',
    showCloseButton: false,
    ...options,
  })
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ShowException = (title: string, err: any) => {
  if (!err) {
    return
  }

  if (err.validation) {
    ErrorMessage(
      `${title} - validation`,
      Object.keys(err.validation)
        .map(
          (validationKey) =>
            `${validationKey}: ${err.validation[validationKey]}`
        )
        .join('\n')
    )
    return
  }

  const message = err['message'] || err['details'] || err['title'] || err

  ErrorMessage(title, message)
}

export const onMaterialSelectorToggleOpen = (isOpen: boolean) => ({
  type: ApplicationActionTypes.OpenMaterialSelector,
  isOpen,
})

export const onNotificationToggle = (shouldOpen = false) => ({
  type: ApplicationActionTypes.ToggleNotifications,
  shouldOpen,
})

export const SetCurrentTabIndex = (tabIndex: number) => ({
  type: ApplicationActionTypes.SetCurrentTabIndex,
  tabIndex,
})

export const SetLoadingMessage = (message: string) => ({
  type: ApplicationActionTypes.SetLoadingMessage,
  message,
})

export const SetAppTheme = (theme) => ({
  type: ApplicationActionTypes.SetAppTheme,
  theme,
})

export const SetDefaultTheme = (themeName: BuiltInThemes) => (dispatch) => {
  localStorage.setItem('rh24BuiltInTheme', themeName)

  switch (themeName) {
    case 'darkBlue':
      dispatch(SetAppTheme(darkBlueTheme))
      break
    default:
      dispatch(SetAppTheme(rh24WebAppTheme))
  }

  dispatch({
    type: ApplicationActionTypes.SetBuiltInTheme,
    themeName,
  })
}
